import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, ArrowLeft, Star, Trophy, Lock, CheckCircle, 
  Sparkles, Target, Flame, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  bronze: { 
    color: 'from-amber-600 to-amber-700', 
    bg: 'bg-amber-50', 
    border: 'border-amber-200',
    text: 'text-amber-800',
    icon: '🥉',
    label: 'Bronze'
  },
  silver: { 
    color: 'from-gray-400 to-gray-500', 
    bg: 'bg-gray-50', 
    border: 'border-gray-200',
    text: 'text-gray-700',
    icon: '🥈',
    label: 'Silver'
  },
  gold: { 
    color: 'from-yellow-400 to-yellow-500', 
    bg: 'bg-yellow-50', 
    border: 'border-yellow-200',
    text: 'text-yellow-800',
    icon: '🥇',
    label: 'Gold'
  },
  diamond: { 
    color: 'from-cyan-400 to-blue-500', 
    bg: 'bg-cyan-50', 
    border: 'border-cyan-200',
    text: 'text-cyan-800',
    icon: '💎',
    label: 'Diamond'
  }
};

const BadgesShowcasePage = ({ user }) => {
  const navigate = useNavigate();
  const [allBadges, setAllBadges] = useState([]);
  const [myBadges, setMyBadges] = useState({ earned: [], total_earned: 0, total_available: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('all');

  useEffect(() => {
    fetchBadges();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBadges = async () => {
    try {
      const token = localStorage.getItem('token');
      const [allRes, myRes] = await Promise.all([
        fetch(`${API_URL}/api/badges/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/badges/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (allRes.ok) setAllBadges(await allRes.json());
      if (myRes.ok) setMyBadges(await myRes.json());
    } catch (error) {
      console.error('Error fetching badges:', error);
    }
    setLoading(false);
  };

  const earnedBadgeIds = myBadges.earned?.map(b => b.id) || [];
  
  const filteredBadges = selectedTier === 'all' 
    ? allBadges 
    : allBadges.filter(b => b.tier === selectedTier);

  const tierCounts = {
    bronze: allBadges.filter(b => b.tier === 'bronze').length,
    silver: allBadges.filter(b => b.tier === 'silver').length,
    gold: allBadges.filter(b => b.tier === 'gold').length,
    diamond: allBadges.filter(b => b.tier === 'diamond').length,
  };

  const earnedByTier = {
    bronze: myBadges.earned?.filter(b => b.tier === 'bronze').length || 0,
    silver: myBadges.earned?.filter(b => b.tier === 'silver').length || 0,
    gold: myBadges.earned?.filter(b => b.tier === 'gold').length || 0,
    diamond: myBadges.earned?.filter(b => b.tier === 'diamond').length || 0,
  };

  const progress = myBadges.total_available > 0 
    ? Math.round((myBadges.total_earned / myBadges.total_available) * 100) 
    : 0;

  const { colors } = useTheme();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="text-2xl font-bold" style={{ color: colors.primary, fontFamily: 'var(--font-heading)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 text-white rounded-b-3xl" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <Award className="w-10 h-10" />
              <div>
                <h1 className="font-heading text-3xl font-bold" style={{ color: "white" }}>Badge Collection</h1>
                <p style={{ color: "rgba(255,255,255,0.9)" }}>Unlock achievements & earn rewards</p>
              </div>
            </div>
          </div>

          {/* Progress Summary */}
          <div className="bg-white/10 backdrop-blur rounded-2xl p-4 mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Your Progress</span>
              <span className="font-bold">{myBadges.total_earned} / {myBadges.total_available} Badges</span>
            </div>
            <Progress value={progress} className="h-3 bg-white/20" />
            <p className="text-xs opacity-75 mt-2 text-center">
              {progress}% Complete - Keep going! 🚀
            </p>
          </div>
        </div>
      </div>

      {/* Tier Stats */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="grid grid-cols-4 gap-2">
          {(['bronze', 'silver', 'gold', 'diamond']).map((tier) => {
            const config = TIER_CONFIG[tier];
            return (
              <Card 
                key={tier}
                className={`p-3 text-center rounded-xl cursor-pointer transition-all ${
                  selectedTier === tier ? `${config.bg} ${config.border} border-2` : 'bg-white'
                }`}
                onClick={() => setSelectedTier(selectedTier === tier ? 'all' : tier)}
                data-testid={`tier-${tier}`}
              >
                <div className="text-2xl mb-1">{config.icon}</div>
                <p className={`text-xs font-semibold ${config.text}`}>{config.label}</p>
                <p className="text-xs text-muted-foreground">
                  {earnedByTier[tier]}/{tierCounts[tier]}
                </p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-4xl mx-auto px-4 pb-2">
        <Tabs value={selectedTier} onValueChange={setSelectedTier}>
          <TabsList className="w-full bg-white rounded-xl p-1">
            <TabsTrigger value="all" className="flex-1 rounded-lg">All</TabsTrigger>
            <TabsTrigger value="earned" className="flex-1 rounded-lg">
              <CheckCircle className="w-4 h-4 mr-1" /> Earned
            </TabsTrigger>
            <TabsTrigger value="locked" className="flex-1 rounded-lg">
              <Lock className="w-4 h-4 mr-1" /> Locked
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Badges Grid */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {(selectedTier === 'earned' 
            ? myBadges.earned || []
            : selectedTier === 'locked'
              ? allBadges.filter(b => !earnedBadgeIds.includes(b.id))
              : filteredBadges
          ).map((badge, index) => {
            const isEarned = earnedBadgeIds.includes(badge.id);
            const config = TIER_CONFIG[badge.tier];
            
            return (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`p-4 rounded-2xl text-center relative overflow-hidden ${
                    isEarned ? config.bg : 'bg-gray-100'
                  } ${isEarned ? config.border : 'border-gray-200'} border-2`}
                  data-testid={`badge-${badge.id}`}
                >
                  {/* Tier indicator */}
                  <div className={`absolute top-2 right-2 text-xs px-2 py-0.5 rounded-full ${
                    isEarned ? `bg-gradient-to-r ${config.color} text-white` : 'bg-gray-200 text-gray-500'
                  }`}>
                    {config.label}
                  </div>
                  
                  {/* Badge icon */}
                  <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl mb-3 ${
                    isEarned 
                      ? `bg-gradient-to-br ${config.color} shadow-lg` 
                      : 'bg-gray-200'
                  }`}>
                    {isEarned ? (
                      badge.icon
                    ) : (
                      <Lock className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Badge info */}
                  <h3 className={`font-bold text-sm mb-1 ${isEarned ? 'text-gray-900' : 'text-gray-400'}`}>
                    {badge.name}
                  </h3>
                  <p className={`text-xs ${isEarned ? 'text-gray-600' : 'text-gray-400'}`}>
                    {badge.description}
                  </p>
                  
                  {/* Points requirement if applicable */}
                  {badge.points_required && !isEarned && (
                    <div className="mt-2 text-xs text-gray-500">
                      <Trophy className="w-3 h-3 inline mr-1" />
                      {badge.points_required} pts needed
                    </div>
                  )}
                  
                  {/* Earned indicator */}
                  {isEarned && (
                    <div className="mt-2 flex items-center justify-center text-xs text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Earned!
                    </div>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredBadges.length === 0 && selectedTier !== 'earned' && selectedTier !== 'locked' && (
          <Card className="p-8 text-center rounded-3xl">
            <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No badges in this category</p>
          </Card>
        )}
      </div>

      {/* How to Earn Section */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <Card className="p-6 rounded-3xl bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            How to Earn Badges
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center text-xs">🥉</div>
              <div>
                <p className="font-medium">Bronze</p>
                <p className="text-gray-600 text-xs">Complete basic tasks like onboarding and first check-ins</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs">🥈</div>
              <div>
                <p className="font-medium">Silver</p>
                <p className="text-gray-600 text-xs">Reach 100 points, complete 3 goals, or get a 3-day streak</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-xs">🥇</div>
              <div>
                <p className="font-medium">Gold</p>
                <p className="text-gray-600 text-xs">Reach 500 points, complete your project, or 7-day streak</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center text-xs">💎</div>
              <div>
                <p className="font-medium">Diamond</p>
                <p className="text-gray-600 text-xs">Reach 1000 points, 14-day streak, or become an All-Star!</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default BadgesShowcasePage;
